import {
	AdminSearchAccountBullet,
	AdminSearchBillToBullet,
	AdminSearchCheckYourSpellingBullet,
	AdminSearchNoResultsFoundHeader,
	AdminSearchNoResultsFoundSubheader,
	AdminSearchResultsHeader,
	AdminSearchUserBullet,
	UnavailableDataPlaceholderText
} from "constants/text.ts";
import { TableBody } from "@mui/material";
import { useNavigate } from "react-router";
import { Fragment, useMemo } from "react";
import SortableTableHead, { HeadCell } from "../Common/Table/SortableTableHead.tsx";
import { useHandleTableSort } from "hooks/useHandleTableSort.ts";
import { StyledTableCell, StyledTableWrapper } from "../Common/Table/StyledTable.styles.ts";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { ClickableTableRow, FilteredAdminTableWrapper, SearchParagraphStyled } from "./AdminSearchTable.styles.ts";
import { NoResultsBox } from "pages/Preferences/NoResultsBox.tsx";
import { AdminSearchResult } from "pages/Preferences/AdminSettings/utils/adminSettingsUtils.ts";

interface AdminSearchTableProps {
	requests?: AdminSearchResult[];
}

const AdminSearchTable = ({ requests }: AdminSearchTableProps) => {
	const navigate = useNavigate();
	const baseNewRequestTableHeadCells = useMemo(
		() =>
			[
				{ id: "name", label: "Name", sortable: true },
				{ id: "number", label: "Number" },
				{ id: "type", label: "Type" },
				{ id: "email", label: "Email" },
				{ id: "open", label: "" }
			].map((cell) => ({
				...cell,
				disablePadding: false,
				numeric: false,
				sortable: cell.sortable ?? false
			})) satisfies HeadCell[],
		[]
	);

	const { getComparator, handleRequestSort, order, orderBy } = useHandleTableSort("name", "asc");
	const filteredRequests = useMemo(() => {
		return requests ? requests.slice().sort(getComparator(order, orderBy)) : [];
	}, [requests, order, orderBy, getComparator]);
	const filteredHeadCells = useMemo(() => baseNewRequestTableHeadCells, [baseNewRequestTableHeadCells]);

	const GetNewRequestTableRows = (request: AdminSearchResult) => {
		const requestTableCells = [
			{
				testId: "admin-search-table-name",
				props: { fixedWidth: "262px" },
				content: request?.name ?? UnavailableDataPlaceholderText
			},
			{
				testId: "admin-search-table-number",
				props: { fixedWidth: "262px" },
				content: request?.number ?? UnavailableDataPlaceholderText
			},
			{
				testId: "admin-search-table-type",
				props: { minWidth: "50px", maxWidth: "262px" },
				content: request?.type ?? UnavailableDataPlaceholderText
			},
			{
				testId: "admin-search-table-email",
				props: { minWidth: "100px", maxWidth: "262px" },
				content: request?.email ?? UnavailableDataPlaceholderText
			}
		];

		return (
			<Fragment>
				{requestTableCells.map(({ testId, props, content }) => (
					<StyledTableCell
						key={testId}
						data-testid={testId}
						{...props}
					>
						{content}
					</StyledTableCell>
				))}

				<StyledTableCell
					minWidth="10px"
					maxWidth="56px"
					align="right"
					isLastIndex
				>
					<ChevronRight />
				</StyledTableCell>
			</Fragment>
		);
	};

	const handleNavigation = (request: AdminSearchResult) => {
		if (request.type === "Person") {
			navigate(`/admin-settings/people/${request.id}`);
		} else if (request.type === "Bill To") {
			navigate(`/admin-settings/accounts/${request.id}/billtos/${request.number}`);
		} else if (request.type === "Account") {
			navigate(`/admin-settings/accounts/${request.id}/billtos`);
		}
	};
	return (
		<>
			{filteredRequests && filteredRequests.length > 0 ? (
				<>
					<SearchParagraphStyled>{AdminSearchResultsHeader}</SearchParagraphStyled>
					<FilteredAdminTableWrapper>
						<StyledTableWrapper
							data-testid="admin-search-table"
							aria-label="Admin Search Table"
						>
							<SortableTableHead
								orderBy={orderBy}
								order={order}
								onRequestSort={handleRequestSort}
								headCells={filteredHeadCells}
							/>
							<TableBody>
								{filteredRequests.map((request: AdminSearchResult, index) => (
									<ClickableTableRow
										key={index}
										data-testid={`request-${request?.id}`}
										onClick={() => handleNavigation(request)}
									>
										{GetNewRequestTableRows(request)}
									</ClickableTableRow>
								))}
							</TableBody>
						</StyledTableWrapper>
					</FilteredAdminTableWrapper>
				</>
			) : (
				<NoResultsBox
					headerText={AdminSearchNoResultsFoundHeader}
					subheaderText={AdminSearchNoResultsFoundSubheader}
					bulletList={[
						AdminSearchUserBullet,
						AdminSearchAccountBullet,
						AdminSearchBillToBullet,
						AdminSearchCheckYourSpellingBullet
					]}
				/>
			)}
		</>
	);
};

export default AdminSearchTable;
